import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"
import { useState } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { AgentMessageContainer } from "@/components/Chat/AgentMessageStyles.ts"
import { SecretsListViewModel, type SecretViewModel } from "@/components/Chat/NewProject/SecretsListViewModel.ts"
import { ServiceListMessageStyles } from "@/components/Chat/NewProject/ServiceListMessageStyles.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { PromptInputStyles } from "@/components/PromptInput/PromptInputStyles.ts"
import { Toggle } from "@/components/Toggle/Toggle.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { useOrgContext } from "@/hooks/useRootContext.ts"
import { CreateRegular, Icon16 } from "@/icons/Icons.ts"
import { SECRET_VALUE_PLACEHOLDER } from "@/lib/displayUtils.ts"
import { AddSecretDialog } from "@/routes/projects/secrets/AddSecretDialog.tsx"
import * as textStyles from "@/styles/textStyles.ts"
import { smallFontSize } from "@/styles/textStyles.ts"

import type { AiChatMessageContent } from "@/lib/client/generated/TypeDefinitions.ts"
import type { IObservableValue } from "mobx"

export const SecretsListMessage = observer((props: {
  taskId: string
  content: AiChatMessageContent.InitRuntimeConfig.SecretList
  isActive: boolean
  isAgentRunning: IObservableValue<boolean>,
  messageId: string
}) => {
  const context = useOrgContext()

  const args = {
    content: props.content,
    context: context,
    taskId: props.taskId,
    agentIsRunning: props.isAgentRunning,
    messageId: props.messageId
  }
  const viewModel = useModel(() => new SecretsListViewModel(args), [args.context, args.content, args.taskId, args.messageId])

  if (viewModel.secrets.get().length === 0) {
    return null
  }

  return (
    <AgentMessageContainer isActive={props.isActive}>
      <Vertical gap="16px">
        <FormSubtitle>
          The following secrets are required to proceed with the preview configuration. Please provide their values.
        </FormSubtitle>

        <Vertical gap="16px">
          {viewModel.secrets.get().map((it) => (
            <SecretItem key={it.key} model={it} />
          ))}
        </Vertical>

        <Horizontal gap="8px" alignSelf="flex-end">
          {viewModel.isManuallyEnteredEditMode.get() && (
            <Button kind="alternative" onClick={() => viewModel.reset()} disabled={viewModel.isSubmitting.get()}>
              Cancel
            </Button>
          )}

          {viewModel.isInEditMode.get() && (
            <ButtonWithIcon
              kind="alternative"
              onClick={() => viewModel.setSecretDialogOpen(true)}
              disabled={!viewModel.canSubmit.get()}
            >
              <CreateRegular className={Icon16} />
              Add secret
            </ButtonWithIcon>
          )}

          {viewModel.isInEditMode.get() && (
            <Button kind="primary" onClick={viewModel.handleContinue} disabled={!viewModel.canSubmit.get()}>
              {viewModel.isManuallyEnteredEditMode.get() ? "Update" : "Continue"}
            </Button>
          )}

          {(!viewModel.isInEditMode.get() && viewModel.canEnterEditMode.get()) && (
            <Button
              kind="alternative"
              onClick={() => viewModel.setIsEditing(true)}
              disabled={viewModel.processing.get()}
            >
              Edit secrets
            </Button>
          )}
        </Horizontal>

        {viewModel.isInEditMode.get() && (
          <AddSecretDialog
            isOpen={viewModel.addSecretDialogOpen.get()}
            onOpenChange={viewModel.setSecretDialogOpen}
            onAddSecret={viewModel.handleAddSecret}
          />
        )}
      </Vertical>
    </AgentMessageContainer>
  )
})

const SecretItem = observer((props: { model: SecretViewModel }) => {
  const model = props.model
  const disabled = model.disabled.get()
  const isEditing = model.isEditing.get()
  const error = model.error.get()

  const [showSecret, setShowSecret] = useState(false)

  return (
    <Vertical className={cx(
      disabled ? Disabled : null,
      ServiceListMessageStyles.Container,
      disabled ? ServiceListMessageStyles.Container_Disabled : ServiceListMessageStyles.Container_Active
    )}>
      <Horizontal justifyContent="space-between" alignItems="center">
        <SecretKey className={Disableable}>{model.key}</SecretKey>
        {isEditing && (
          <Tooltip content={disabled ? "Use secret" : "Skip secret"}>
            <Toggle active={!disabled} onClick={() => model.setDisabled(!disabled)} />
          </Tooltip>
        )}
      </Horizontal>

      {model.description && model.description.length > 0 && (
        <SecretDescription className={Disableable}>
          {model.description}
        </SecretDescription>
      )}

      {!disabled && isEditing && (
        <CommentInputWrapper className={Disableable}>
          <InputContainer className={PromptInputStyles.Container}>
            <Input
              type={showSecret ? "text" : "password"}
              value={model.value.get()}
              onChange={model.handleChange}
              placeholder="Enter secret value"
              disabled={disabled}
              onFocus={model.handleFocus}
              onBlur={model.handleBlur}
              readOnly={!isEditing}
              className={PromptInputStyles.Input}
            />

            <ToggleSecret
              size="small"
              kind="alternative"
              disabled={disabled || model.value.get().length === 0 || model.value.get() === SECRET_VALUE_PLACEHOLDER}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setShowSecret(!showSecret)
              }}
            >
              {showSecret ? "Hide" : "Show"}
            </ToggleSecret>
          </InputContainer>
        </CommentInputWrapper>
      )}

      {error && (
        <Error>
          {error}
        </Error>
      )}
    </Vertical>
  )
})

const Disableable = css`
`

const Disabled = css`
  .${Disableable} {
    opacity: 0.4;

    * {
      color: var(--text-color) !important;
    }
  }
`

const FormSubtitle = styled.div`
  text-align: center;
`

const SecretKey = styled.div`
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
`

const SecretDescription = styled.div`
  ${textStyles.smallFontSize};

  font-weight: 400;
`

const InputContainer = styled.div`
  position: relative;
  margin-top: 8px;
`

const CommentInputWrapper = styled.div`
  width: 100%;
`

const Input = styled.input`
  ${textStyles.smallFontSize};
  ${textStyles.breakWord};

  width: 100%;
  min-height: 36px;
  padding: 12px 16px;

  border: none;
`

const Error = styled.span`
  ${textStyles.smallFontSize};

  color: var(--error-color);
`

const ButtonWithIcon = styled(Button)`
  gap: 4px;
`

const ToggleSecret = styled(Button)`
  position: absolute;
  right: 16px; /* Same as the padding of Input */
  top: 50%;

  transform: translateY(-50%);

  height: auto;
  border: none;
  padding: 0;

  ${smallFontSize};

  color: var(--secondary-text-color);

  &:hover:not(:disabled) {
    color: var(--secondary-text-color);
    background: var(--background-color);
  }
`
